import { useEffect } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
  TimeScale,
} from "chart.js";
import { Line } from "react-chartjs-2";
import "chartjs-adapter-date-fns";
import {
  getPracticeGraphSingleSeriesGrey,
  getPracticeGraphUiColors,
} from "utils/constants";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
  TimeScale
);

export interface DataSeriesItem {
  label: string;
  data: any[];
  borderColor: string;
}

interface IProps {
  theme: "light" | "dark";
  blueFilter: boolean;
  greenFilter: boolean;
  purpleFilter: boolean;
  maxScore: number;
  isBeginningZero: boolean;
  guaranteeScoreData: any;
  scoreGoalData: any;
  dataPoints: any;
  dataSeries?: DataSeriesItem[];
  roundMin?: number;
  roundMax?: number;
}

export const AreaGraph = ({
  theme,
  blueFilter = true,
  greenFilter,
  purpleFilter,
  maxScore,
  isBeginningZero,
  guaranteeScoreData,
  scoreGoalData,
  dataPoints,
  dataSeries,
  roundMin,
  roundMax,
}: IProps) => {
  const useMultipleSeries = dataSeries && dataSeries.length > 0;
  const { tooltipBorderColor: defaultTooltipBorderColor, pointBackgroundColor } =
    getPracticeGraphUiColors(theme);
  const allPoints = useMultipleSeries
    ? dataSeries!.flatMap((s) => s.data)
    : dataPoints;

  const scoreDatasets = useMultipleSeries
    ? dataSeries!.map((series) => ({
        label: series.label,
        data: series.data,
        borderColor: series.borderColor,
        backgroundColor: "transparent",
        borderWidth: 3,
        fill: false,
        pointRadius: 4,
        pointBorderWidth: 2,
        pointBorderColor: series.borderColor,
        pointBackgroundColor,
      }))
    : [
        {
          fill: purpleFilter ? true : false,
          label: "Dataset 2",
          data: dataPoints,
          borderColor: getPracticeGraphSingleSeriesGrey(theme),
          backgroundColor:
            theme === "dark"
              ? (context: any) => {
                  if (!context.chart.chartArea) {
                    return;
                  }
                  const {
                    ctx,
                    chartArea: { top, bottom },
                  } = context.chart;
                  const gradientBg = ctx.createLinearGradient(0, top, 0, bottom);
                  gradientBg.addColorStop(0, "#CB91E7");
                  gradientBg.addColorStop(1, "rgba(207, 111, 252, 0.00)");
                  return gradientBg;
                }
              : "rgba(119, 23, 193, 0.22)",
          pointRadius: 4,
          pointBorderWidth: 2,
          pointBorderColor: getPracticeGraphSingleSeriesGrey(theme),
          pointBackgroundColor,
        },
      ];

  const data = {
    datasets: [
      ...scoreDatasets,
      ...(blueFilter
        ? [
            {
              label: "Goal",
              data: scoreGoalData,
              borderColor: "#3272BE",
              borderWidth: 4,
              borderDash: [5, 5],
              fill: false,
              yAxisID: "y",
              pointRadius: 0,
              pointHitRadius: 0,
            },
          ]
        : []),
      ...(greenFilter
        ? [
            {
              label: "Guarantee Score",
              data: guaranteeScoreData,
              borderColor: "#71BF8F",
              borderWidth: 4,
              borderDash: [5, 5],
              fill: false,
              yAxisID: "y",
              pointRadius: 0,
              pointHitRadius: 0,
            },
          ]
        : []),
    ],
  };

  const dynamicTooltipBorder = {
    id: "dynamicTooltipBorder",
    beforeTooltipDraw(chart: any) {
      const tooltip = chart.tooltip;
      if (tooltip?.dataPoints?.length > 0) {
        const borderColor = tooltip.dataPoints[0].dataset.borderColor ?? defaultTooltipBorderColor;
        tooltip.options.borderColor = borderColor;
      } else {
        tooltip.options.borderColor = defaultTooltipBorderColor;
      }
    },
  };

  const hoverLine = {
    id: "hoverLine",
    afterDatasetsDraw(chart: any, args: any, plugins: any) {
      const {
        ctx,
        tooltip,
        chartArea: { top, bottom, left, right, width, height },
      } = chart;
      if (tooltip && tooltip._active && tooltip._active.length > 0) {
        const xCoor = tooltip._active[0].element.x;
        const yCoor = tooltip._active[0].element.y;
        ctx.save();
        ctx.lineWidth = 3;
        ctx.strokeStyle = theme === "light" ? "black" : "#FFF";
        ctx.setLineDash([6, 6]);
        ctx.beginPath();
        ctx.moveTo(xCoor, yCoor);
        ctx.lineTo(xCoor, bottom);
        ctx.stroke();
        ctx.closePath();
        ctx.restore();
      }
    },
  };

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        displayColors: false,
        borderWidth: useMultipleSeries ? 2 : 0,
        borderColor: defaultTooltipBorderColor,
        callbacks: {
          label: (tooltipItem: any) => {
            const dataPoint = tooltipItem.raw;
            const displayScore =
              dataPoint?.score != null
                ? dataPoint.score
                : tooltipItem.parsed.y;
            if (dataPoint && dataPoint.testName) {
              return [dataPoint.testName, displayScore.toString()];
            }
            return displayScore.toString();
          },
          title: function (tooltipItems: any) {
            return "";
          },
          labelColor: (tooltipItem: any) => ({
            borderColor: tooltipItem.dataset?.borderColor ?? defaultTooltipBorderColor,
            backgroundColor: tooltipItem.dataset?.borderColor ?? defaultTooltipBorderColor,
          }),
        },
        xAlign: "center",
        yAlign: "bottom",
        bodyAlign: "center",
        padding: 8,
      },
    },
    scales: {
      y: {
        border: {
          color: theme === "light" ? "#BCBCBD" : "#fff",
        },
        min: roundMin,
        max: roundMax,
        beginAtZero: isBeginningZero,
        ticks: {
          stepSize: 10,
          color: theme === "light" ? "#6B6B6B" : "#fff",
        },
      },
      x: {
        type: "time" as any,
        border: {
          color: theme === "light" ? "#BCBCBD" : "#fff",
        },
        time: {
          unit: "month",
          displayFormats: {
            month: "MMM",
          },
          min:
            allPoints.length === 1
              ? new Date(allPoints[0].x.getTime() - 30 * 24 * 60 * 60 * 1000)
              : undefined,
          max:
            allPoints.length === 1
              ? new Date(allPoints[0].x.getTime() + 30 * 24 * 60 * 60 * 1000)
              : undefined,
        },
        grid: {
          display: false,
        },
        ticks: {
          color: theme === "light" ? "#6B6B6B" : "#fff",
          source: "data",
          autoSkip: false,
        },
      },
    },
  };
  return <Line options={options} data={data} plugins={[dynamicTooltipBorder, hoverLine]} />;
};
